import { ArrowLeftIcon, CheckIcon, ChevronRightIcon } from "lucide-react";
import { ReactNode, useEffect, useMemo, useState } from "react";

import { ChooseEntityDialogProvider } from "@/components/ChooseEntityDialog";
import ChooseEntityViewSingle from "@/components/ChooseEntityDialog/ChooseEntityDialogContent/ChooseEntityViewSingle";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityIcon } from "@/components/ui/icons";
import SearchBar from "@/components/ui/SearchBar";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { getItemColor } from "@/components/UnifiedRegistry/utils";
import { cn } from "@/lib/utils";
import { Entity, EntityType } from "@/queries";
import {
  ColumnType,
  CompositionSourceColumn,
  CompositionSourceGroup,
} from "@/queries/smart-tables/types";
import { SmartColumnHeaderIcon } from "@/routes/smart-table/components/SmartTablePage/ColumnIcons";
import {
  encodeSourceResolver,
  parseSourceResolver,
} from "@/routes/smart-table/utils/sourceResolver";

// ──────────────────────────────────────────────────────────────────────
// Overview
//
// A three-step source picker that reuses the visual idiom of
// `ChooseEntityDialog` and `ChooseEntityVariantView` (rounded-icon
// header, "← Back" in the title row, quiet list items with a trailing
// check). The three steps are:
//
//   1. Table  — delegates to the existing `ChooseEntityDialog` for the
//               SMART_TABLE entity so users get the full workspace
//               registry UI (folders, search, recency) they see
//               everywhere else.
//   2. Sheet  — secondary state, rendered inside our own `Dialog`
//               wrapper. Always mounts with a Table breadcrumb in the
//               header so the user knows what they're inside.
//   3. Column — tertiary state with a full Table / Sheet trail.
//
// The three steps are mutually exclusive: at any point exactly ONE
// dialog is mounted. This is intentional — nested/stacked dialogs are a
// known usability hazard (focus traps, Escape-key ambiguity, backdrop
// occlusion), and for a picker flow like this it's cleaner to swap the
// mount than to stack it.
//
// Non-technical framing: copy never mentions "resolver" / "reference"
// / "binding". Headers ask questions ("Which sheet?", "Which column?").
// The host block shows the current pick as a two-line card (column
// name above, "Sheet · Table" caption below) that reopens this dialog
// on click. Icons carry column-type information so we don't have to
// say "TEXT column" etc. in body copy.
// ──────────────────────────────────────────────────────────────────────

export interface ChosenSource {
  /** Canonical `{smart_table_id}.{sheet_id}.{column_id}` resolver string. */
  resolver: string;
  smartTableId: string;
  smartTableTitle: string;
  sheetId: string;
  sheetTitle: string;
  columnId: string;
  columnTitle: string;
  columnType: ColumnType;
}

interface ChooseSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /**
   * Every composable column the current workspace can reach. Handed in
   * whole so the dialog doesn't need to know anything about the
   * discovery hook — the host block owns fetching and scoping.
   */
  compositionGroups: CompositionSourceGroup[];

  /**
   * Ambient "current" pointers used to prune self-references (the
   * backend `ck_smart_column_dependencies_no_self_ref` constraint
   * rejects columns that reference themselves) and to render the
   * "This table" badge on the table segment of the trail.
   */
  currentTableId?: string;
  currentColumnId?: string;

  /** Active binding (if any), used to seed the dialog mid-flow. */
  value?: string;
  values?: string[];
  /** Preferred resolver when opened from a specific summary row. */
  initialResolver?: string;

  /** Cached titles from the host block config — shown while the
   *  composition catalog is still loading / has a stale row. */
  fallbackTableTitle?: string;
  fallbackSheetTitle?: string;

  onSelect: (chosen: ChosenSource | ChosenSource[]) => void;
}

type Step = "table" | "sheet" | "column";

// ──────────────────────────────────────────────────────────────────────
// Visual primitives
// ──────────────────────────────────────────────────────────────────────

/**
 * Shared header + body shell for the sheet and column panes. Mirrors
 * the geometry of `ChooseEntityVariantView`'s header (back button +
 * rounded-color icon + title) so the secondary/tertiary steps feel like
 * a natural continuation of the workspace registry dialog.
 */
const PanelShell = ({
  title,
  description,
  trail,
  onBack,
  onCancel,
  children,
}: {
  title: string;
  description?: string;
  trail?: ReactNode;
  onBack: () => void;
  onCancel: () => void;
  children: ReactNode;
}) => (
  <div className="flex h-full min-h-0 flex-col">
    <DialogHeader className="space-y-2 border-b border-gray-200 px-6 py-4">
      <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          aria-label="Back"
          className="h-10 w-10 flex-shrink-0 p-0"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <div
          className={cn(
            "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full",
            getItemColor(EntityType.SMART_TABLE),
          )}
        >
          <EntityIcon type={EntityType.SMART_TABLE} size="md" />
        </div>
        <span className="min-w-0 flex-1 truncate">{title}</span>
      </DialogTitle>
      {description && (
        <DialogDescription className="pl-[52px] text-sm text-gray-600">
          {description}
        </DialogDescription>
      )}
      {trail && <div className="pl-[52px]">{trail}</div>}
    </DialogHeader>

    <div className="min-h-0 flex-1 overflow-hidden">{children}</div>

    <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
      <Button variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  </div>
);

/**
 * Notion-style inline breadcrumb. Used in the header of the sheet and
 * column steps so users always see the full path they've built so far.
 *
 * Segments are intentionally non-interactive: the `← Back` button
 * already provides step-reversal, and giving users a second path to
 * change the same pointer tends to confuse non-technical users
 * (and invites the "which one is the real back?" question).
 */
const Trail = ({ segments }: { segments: { badge?: ReactNode; label: string }[] }) => (
  <div className="flex items-center gap-1 text-xs text-gray-500">
    {segments.map((segment, idx) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={idx} className="flex min-w-0 items-center gap-1">
        {idx > 0 && (
          <ChevronRightIcon className="h-3 w-3 flex-shrink-0 text-gray-300" aria-hidden />
        )}
        {segment.badge}
        <OverflowDisplayTooltip content={segment.label} className="min-w-0">
          <span className="block truncate">{segment.label}</span>
        </OverflowDisplayTooltip>
      </div>
    ))}
  </div>
);

const ThisTableBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full bg-cyan-50 px-1.5 py-0.5 text-[10px] font-medium text-cyan-700">
    This table
  </span>
);

const EmptyState = ({ title, description }: { title: string; description: string }) => (
  <div className="flex h-full items-center justify-center p-6">
    <div className="text-center">
      <div className="text-sm font-medium text-gray-900">{title}</div>
      <div className="mt-1 max-w-sm text-sm text-gray-500">{description}</div>
    </div>
  </div>
);

const SearchHeader = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) => (
  <div className="border-b border-gray-100 px-4 py-2">
    <SearchBar
      value={value}
      onChange={onChange}
      placeholderPhrasesArray={[placeholder]}
      size="xs"
      className="w-full"
      inputClassName="bg-transparent"
    />
  </div>
);

const ColumnGlyph = ({
  columnType,
  columnConfig,
  selected = false,
}: {
  columnType?: ColumnType;
  columnConfig?: Record<string, unknown> | null;
  selected?: boolean;
}) => (
  <SmartColumnHeaderIcon
    columnType={columnType}
    config={columnConfig}
    tone={selected ? "selected" : "default"}
    className="h-5 w-5"
  />
);

// ──────────────────────────────────────────────────────────────────────
// Sheet step
// ──────────────────────────────────────────────────────────────────────

interface SheetListItem {
  id: string;
  title: string;
  columnCount: number;
}

const SheetList = ({
  sheets,
  selectedSheetId,
  onSelect,
}: {
  sheets: SheetListItem[];
  selectedSheetId?: string;
  onSelect: (sheet: SheetListItem) => void;
}) => {
  const [query, setQuery] = useState("");

  // Search only shows up past a threshold — for small lists it's pure
  // noise, and non-technical users feel the extra chrome more than
  // power users do.
  const showSearch = sheets.length > 5;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return sheets;
    }
    return sheets.filter(s => s.title.toLowerCase().includes(q));
  }, [query, sheets]);

  if (sheets.length === 0) {
    return (
      <EmptyState
        title="No sheets available"
        description="This table doesn't have any sheets you can pull values from yet."
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      {showSearch && <SearchHeader value={query} onChange={setQuery} placeholder="Search sheets" />}

      {filtered.length === 0 ? (
        <EmptyState title="No matching sheets" description="Try a different search." />
      ) : (
        <div className="h-full overflow-y-auto p-3">
          <ul className="space-y-1">
            {filtered.map(sheet => {
              const isSelected = sheet.id === selectedSheetId;
              return (
                <li key={sheet.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(sheet)}
                    className={cn(
                      "group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                      "transition-colors hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none",
                      isSelected && "bg-gray-100",
                    )}
                  >
                    <div
                      className={cn(
                        "flex flex-shrink-0 items-center justify-center text-gray-400",
                        isSelected && "text-gray-600",
                      )}
                    >
                      <EntityIcon type={EntityType.SMART_TABLE} size="sm" />
                    </div>
                    <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                      <OverflowDisplayTooltip content={sheet.title} className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-gray-700">
                          {sheet.title}
                        </span>
                      </OverflowDisplayTooltip>
                      <span className="flex-shrink-0 text-xs text-gray-400">
                        {sheet.columnCount} {sheet.columnCount === 1 ? "col" : "cols"}
                      </span>
                    </div>
                    {isSelected && (
                      <CheckIcon className="h-4 w-4 flex-shrink-0 text-gray-600" aria-hidden />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Column step
// ──────────────────────────────────────────────────────────────────────

interface ColumnListItem extends CompositionSourceColumn {
  resolver: string;
  selected?: boolean;
}

const ColumnList = ({
  columns,
  selectedResolver,
  selectedResolvers,
  onSelect,
}: {
  columns: ColumnListItem[];
  selectedResolver?: string;
  selectedResolvers?: string[];
  onSelect: (columns: ColumnListItem[]) => void;
}) => {
  const [query, setQuery] = useState("");
  const [internalSelected, setInternalSelected] = useState<string[]>([]);

  useEffect(() => {
    if (selectedResolvers && selectedResolvers.length > 0) {
      setInternalSelected(selectedResolvers);
    } else if (selectedResolver) {
      setInternalSelected([selectedResolver]);
    } else {
      const initiallySelected = columns.filter(c => c.selected).map(c => c.resolver);
      setInternalSelected(initiallySelected);
    }
  }, [columns, selectedResolver, selectedResolvers]);

  const showSearch = columns.length > 5;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return columns;
    }
    return columns.filter(c => c.title.toLowerCase().includes(q));
  }, [columns, query]);

  if (columns.length === 0) {
    return (
      <EmptyState
        title="No columns available"
        description="This sheet doesn't have any columns you can pull values from yet."
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      {showSearch && (
        <SearchHeader value={query} onChange={setQuery} placeholder="Search columns" />
      )}

      {filtered.length === 0 ? (
        <EmptyState title="No matching columns" description="Try a different search." />
      ) : (
        <div className="h-full overflow-y-auto p-3">
          <ul className="space-y-1">
            {filtered.map(column => {
              const isSelected = internalSelected.includes(column.resolver);
              return (
                <li key={column.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        setInternalSelected(prev => prev.filter(r => r !== column.resolver));
                      } else {
                        setInternalSelected(prev => [...prev, column.resolver]);
                      }
                    }}
                    className={cn(
                      "group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                      "transition-colors hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none",
                      isSelected && "bg-gray-100",
                    )}
                  >
                    <ColumnGlyph
                      columnType={column.type}
                      columnConfig={column.config}
                      selected={isSelected}
                    />
                    <OverflowDisplayTooltip content={column.title} className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-gray-700">
                        {column.title}
                      </span>
                    </OverflowDisplayTooltip>
                    {isSelected && (
                      <CheckIcon className="h-4 w-4 flex-shrink-0 text-gray-600" aria-hidden />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <div className="sticky bottom-0 mt-auto border-t border-gray-200 bg-white p-4">
        <Button
          type="button"
          onClick={() => {
            const selectedCols = columns.filter(c => internalSelected.includes(c.resolver));
            onSelect(selectedCols);
          }}
          disabled={internalSelected.length === 0}
          className="w-full"
        >
          Confirm Selection ({internalSelected.length})
        </Button>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────

const ChooseSourceDialog = ({
  open,
  onOpenChange,
  compositionGroups,
  currentTableId,
  currentColumnId,
  value,
  values,
  initialResolver,
  fallbackTableTitle,
  fallbackSheetTitle,
  onSelect,
}: ChooseSourceDialogProps) => {
  // Local draft of the (table, sheet) pointer the user is navigating
  // through. Kept separate from the host's persisted configuration so
  // back/forward navigation feels instant and so the user can abandon
  // mid-flow (close the dialog) without mutating the block's state.
  const [draftTableId, setDraftTableId] = useState<string | undefined>(undefined);
  const [draftTableTitle, setDraftTableTitle] = useState<string | undefined>(undefined);
  const [draftSheetId, setDraftSheetId] = useState<string | undefined>(undefined);
  const [draftSheetTitle, setDraftSheetTitle] = useState<string | undefined>(undefined);

  // Which of the three panels is on screen. Mutually exclusive — exactly
  // one of { table-picker, our outer dialog on "sheet", our outer dialog
  // on "column" } is mounted at any time.
  const [step, setStep] = useState<Step>("sheet");

  // Seed the drafts + step whenever the dialog opens. Honors (in order):
  //   1. The caller-provided `initialResolver` (used when opening from
  //      a specific summary row).
  //   2. The currently-persisted `value` / first item in `values`
  //      (resumes where the user left off).
  //   3. The caller-provided fallback titles (handles deleted/stale
  //      references so the prior path remains visible).
  //   4. The ambient current table (default for a brand-new block).
  useEffect(() => {
    if (!open) {
      return;
    }

    const selectedValue = initialResolver ?? value ?? values?.[0];
    const parsed = selectedValue ? parseSourceResolver(selectedValue) : null;
    if (parsed) {
      const group = compositionGroups.find(
        g => g.smart_table_id === parsed.smartTableId && g.sheet_id === parsed.sheetId,
      );
      setDraftTableId(parsed.smartTableId);
      setDraftTableTitle(group?.smart_table_title ?? fallbackTableTitle);
      setDraftSheetId(parsed.sheetId);
      setDraftSheetTitle(group?.sheet_title ?? fallbackSheetTitle);
      setStep("column");
      return;
    }

    if (currentTableId) {
      const group = compositionGroups.find(g => g.smart_table_id === currentTableId);
      setDraftTableId(currentTableId);
      setDraftTableTitle(group?.smart_table_title ?? fallbackTableTitle ?? "This table");
      setDraftSheetId(undefined);
      setDraftSheetTitle(undefined);
      setStep("sheet");
      return;
    }

    setDraftTableId(undefined);
    setDraftTableTitle(undefined);
    setDraftSheetId(undefined);
    setDraftSheetTitle(undefined);
    setStep("table");
  }, [
    open,
    value,
    values,
    initialResolver,
    currentTableId,
    compositionGroups,
    fallbackTableTitle,
    fallbackSheetTitle,
  ]);

  // ── Derived data ─────────────────────────────────────────────────

  const availableTableIdSet = useMemo(
    () => new Set(compositionGroups.map(g => g.smart_table_id)),
    [compositionGroups],
  );

  const sheetsForDraftTable = useMemo<SheetListItem[]>(() => {
    if (!draftTableId) {
      return [];
    }
    return compositionGroups
      .filter(g => g.smart_table_id === draftTableId)
      .map(g => ({
        id: g.sheet_id,
        title: g.sheet_title,
        // Column count excludes self-reference so the preview number
        // matches what the user will actually see on the next step.
        columnCount: g.columns.filter(c => !currentColumnId || c.id !== currentColumnId).length,
      }))
      .filter(s => s.columnCount > 0)
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [compositionGroups, currentColumnId, draftTableId]);

  const columnsForDraftSheet = useMemo<ColumnListItem[]>(() => {
    if (!draftTableId || !draftSheetId) {
      return [];
    }
    const group = compositionGroups.find(
      g => g.smart_table_id === draftTableId && g.sheet_id === draftSheetId,
    );
    if (!group) {
      return [];
    }
    return group.columns
      .filter(column => !currentColumnId || column.id !== currentColumnId)
      .map(column => ({
        ...column,
        resolver: encodeSourceResolver(group.smart_table_id, group.sheet_id, column.id),
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [compositionGroups, currentColumnId, draftSheetId, draftTableId]);

  const isDraftCurrentTable = Boolean(
    draftTableId && currentTableId && draftTableId === currentTableId,
  );

  // ── Handlers ─────────────────────────────────────────────────────

  const handleTableSelected = (entity: Entity) => {
    const id = String(entity.id);
    // Switching tables mid-flow resets the sheet pointer — a sheet in a
    // different table is meaningless.
    setDraftTableId(id);
    setDraftTableTitle(entity.name);
    setDraftSheetId(undefined);
    setDraftSheetTitle(undefined);
    setStep("sheet");
  };

  const handleSheetSelected = (sheet: SheetListItem) => {
    setDraftSheetId(sheet.id);
    setDraftSheetTitle(sheet.title);
    setStep("column");
  };

  const handleColumnSelected = (columns: ColumnListItem[]) => {
    if (!draftTableId || !draftTableTitle || !draftSheetId || !draftSheetTitle) {
      return;
    }
    const chosen = columns.map(column => ({
      resolver: column.resolver,
      smartTableId: draftTableId as string,
      smartTableTitle: draftTableTitle as string,
      sheetId: draftSheetId as string,
      sheetTitle: draftSheetTitle as string,
      columnId: column.id,
      columnTitle: column.title,
      columnType: column.type,
    }));
    onSelect(chosen);
    onOpenChange(false);
  };

  // From the sheet step, "back" means "pick a different table".
  // Swapping `step` unmounts our outer dialog and mounts the registry
  // dialog on the next render — cleaner than stacking them.
  const handleBackFromSheet = () => {
    setStep("table");
  };

  // From the column step, "back" means "pick a different sheet in the
  // same table". We keep the draft table intact and just drop the sheet
  // so the sheet list shows up again.
  const handleBackFromColumn = () => {
    setDraftSheetId(undefined);
    setDraftSheetTitle(undefined);
    setStep("sheet");
  };

  const isTableDisabled = useMemo(
    () => (entity: Entity) => !availableTableIdSet.has(String(entity.id)),
    [availableTableIdSet],
  );

  const tableEntityShim = useMemo<Entity<EntityType.SMART_TABLE> | undefined>(() => {
    if (!draftTableId) {
      return undefined;
    }
    return {
      id: draftTableId,
      name: draftTableTitle ?? "Untitled table",
      type: EntityType.SMART_TABLE,
      path: [],
    };
  }, [draftTableId, draftTableTitle]);

  // ── Render ────────────────────────────────────────────────────────

  const tableLabel = draftTableTitle ?? "This table";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-full max-h-[720px] max-w-4xl flex-col gap-0 p-0">
        {step === "table" && (
          <ChooseEntityDialogProvider
            type={EntityType.SMART_TABLE}
            selectedEntity={tableEntityShim}
            disabled={isTableDisabled}
            mode="single"
          >
            <ChooseEntityViewSingle
              type={EntityType.SMART_TABLE}
              currentFolderId={null}
              selectedEntity={tableEntityShim}
              onSelect={handleTableSelected}
              onClose={() => {
                // intentionally empty
              }}
            />
          </ChooseEntityDialogProvider>
        )}

        {step === "sheet" && (
          <PanelShell
            title="Which sheet?"
            description="Pick the sheet that holds the values you want to pull in."
            trail={
              <Trail
                segments={[
                  {
                    badge: isDraftCurrentTable ? <ThisTableBadge /> : undefined,
                    label: tableLabel,
                  },
                ]}
              />
            }
            onBack={handleBackFromSheet}
            onCancel={() => onOpenChange(false)}
          >
            <SheetList
              sheets={sheetsForDraftTable}
              selectedSheetId={draftSheetId}
              onSelect={handleSheetSelected}
            />
          </PanelShell>
        )}

        {step === "column" && draftSheetTitle && (
          <PanelShell
            title="Which column?"
            description="The values from this column will appear here, row by row."
            trail={
              <Trail
                segments={[
                  {
                    badge: isDraftCurrentTable ? <ThisTableBadge /> : undefined,
                    label: tableLabel,
                  },
                  { label: draftSheetTitle },
                ]}
              />
            }
            onBack={handleBackFromColumn}
            onCancel={() => onOpenChange(false)}
          >
            <ColumnList
              columns={columnsForDraftSheet}
              selectedResolver={value}
              selectedResolvers={values}
              onSelect={handleColumnSelected}
            />
          </PanelShell>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChooseSourceDialog;
